import React, { useEffect } from "react";
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/animated-tabs';
import { ROUTES_CONFIG } from '@/config/routes.config';
import { useTypedParams } from 'react-router-typesafe-routes';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '@/trpc';
import { useState } from 'react';
import Dashboard from './(tabs)/dashboard';
import { convertNumberToMarketValue } from '@/lib/utils';
import Reports from './(tabs)/reports';
import { Link } from 'react-router-dom';
import { hasRight } from '@/config/rights.config'
import { useAuth } from '@/hooks/use-auth'
import * as Icon from 'lucide-react'
import { Button } from '@/components/ui/button';
import SelectReportTemplate from '@/components/features/reports/templates/select';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import FillTask from "@/components/features/tasks/fill";

const PlayerDetailPage = () => {
    const { trpc } = useTRPC();
    const { player_id } = useTypedParams(ROUTES_CONFIG.APP.PLAYER_DETAIL);

    const [tab, setTab] = useState<string>('dashboard');
    const [selectedReportTemplate, setSelectedReportTemplate] = React.useState<string | undefined>(undefined);

    const [createdTask, setCreatedTask] = React.useState<string>("");
    const [fillTaskOpen, setFillTaskOpen] = React.useState(false);

    const { data:player } = useQuery({
        queryKey: ['players', player_id],
        queryFn: async () => {
            const player = await trpc.players.getById.query({ player_id: player_id });
            return player;
        },
    })

    const { user } = useAuth();

    const { mutate: createTask, isLoading: isCreatingTask } = useMutation({
        mutationFn: async (data: {
            assigned_user_id: string;
            report_id: string;
            club_id?: string;
            mongo_player_id?: string;
            ext_club_id?: string;

    })  => {
        return await trpc.tasks.create.mutate(data);
    },
    onSuccess: (task) => {
        setFillTaskOpen(true);
        setCreatedTask(task.id);
        setSelectedReportTemplate(undefined);
    },
    onError: (err) => {
        if(err instanceof Error) {
            toast.error(err.message);
        } else {
            toast.error("Er is een onbekende fout opgetreden.");
        }
    }
    });

    const handleReportTemplateSelect = (templateId: string | undefined) => {
        setSelectedReportTemplate(templateId);
        if (templateId && player?.id) {
            createTask({
                assigned_user_id: user?.id || "",
                report_id: templateId,
                mongo_player_id: player.id,
                ext_club_id: player?.clubId || ""
            })
        }
    };

    return (
    <div className='p-4 flex flex-col gap-4 justify-between h-full'>
        <Link 
            to={ROUTES_CONFIG.APP.PLAYER_DB.$buildPath({})}
            className='text-sm font-medium text-foreground/50 hover:text-foreground hover:underline w-fit flex items-center gap-2'
        >
            <Icon.ArrowLeft className="w-4 h-4"/>
            Terug
        </Link>
        <header className='flex items-center gap-10'>
            <div className='size-32 aspect-square relative'>
                <div className='h-full w-full bg-foreground/10 rounded-full flex items-center justify-center outline-2 outline-bright outline-offset-4'>
                    <Avatar className='w-full h-full'>
                        <AvatarImage className='object-cover' src={player?.imageUrl ?? undefined} alt="" />
                        <AvatarFallback className='bg-foreground/10 text-3xl opacity-50'>{player?.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                </div>
                <div className='size-10 absolute -right-2 -bottom-2 aspect-square bg-foreground flex items-center justify-center rounded-full border-background border-2'>
                    <p className='text-lg text-background text-center font-medium'>{player?.shirtNumber?.replace('#','') || "#"}</p>
                </div>
            </div>

            <div className='flex flex-col w-full gap-2'>

                <div className='flex items-center gap-3'>
                    <p className='text-3xl font-semibold'>{player?.name}</p>
                    <div className='h-full min-h-10 w-px bg-foreground/20'></div>
                    <div className='flex items-center gap-2'>
                        <Avatar className='size-8 aspect-square'>
                            <AvatarImage className='object-cover' src={player?.club?.imageUrl ?? undefined} alt="" />
                            <AvatarFallback className='bg-foreground/10 opacity-50'>{player?.club?.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <p className='text-lg'>{player?.club?.name}</p>
                    </div>
                </div>
                <div className='flex items-center justify-between gap-4'>
                    <div className='py-2 px-4 bg-foreground/10 rounded-md flex items-center gap-4 w-fit'>
                        <div className='flex items-center gap-3'>
                            <p className='text-sm text-foreground/50'>Leeftijd</p>
                            <p>{player?.age || "-"}</p>
                        </div>

                        <div className='h-full min-h-6 w-px bg-foreground/20'></div>

                        <div className='flex items-center gap-3'>
                            <p className='text-sm text-foreground/50'>Pos.</p>
                            <p>{player?.positionMain || "-"}</p>
                        </div>

                        <div className='h-full min-h-6 w-px bg-foreground/20'></div>

                        <div className='flex items-center gap-3'>
                            <p className='text-sm text-foreground/50'>Voet</p>
                            <p>{player?.foot || "-"}</p>
                        </div>

                        <div className='h-full min-h-6 w-px bg-foreground/20'></div>

                        <div className='flex items-center gap-3'>
                            <p className='text-sm text-foreground/50'>MW</p>
                            <p>{convertNumberToMarketValue(player?.marketValue) || "-"}</p>
                        </div>
                    </div>
                    {hasRight(user?.role?.name, "TASKS.WRITE") && 
                        <>
                        <SelectReportTemplate
                                value={selectedReportTemplate}
                                onValueChange={handleReportTemplateSelect}
                                trigger={<Button variant='bright' disabled={isCreatingTask}>
                                    {isCreatingTask ? (
                                        <Icon.Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Icon.Plus className="w-4 h-4" />
                                    )}
                                    Nieuw rapport
                                </Button>} 
                                />
                                  {fillTaskOpen ? <FillTask task_id={createdTask} open={true} onOpenChange={setFillTaskOpen} /> : null}
                        </>
                    }
                </div>
            </div>
        </header>
        <main className='bg-foreground/10 h-full overflow-auto rounded-lg'>
            <div className='flex flex-col justify-between h-full'>
                <div className='border-b p-2'>
                    <Tabs value={tab} onValueChange={setTab}>
                        <TabsList className='p-0'>
                            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
                            {player?.id && <TabsTrigger value="reports">Rapporten</TabsTrigger>}
                            <TabsTrigger disabled className='opacity-50 cursor-not-allowed' value="matches">Matchen</TabsTrigger>
                            <TabsTrigger disabled className='opacity-50 cursor-not-allowed' value="info">Uitgebreide info</TabsTrigger>
                            <TabsTrigger disabled className='opacity-50 cursor-not-allowed' value="notes">Notities</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
                <div className='h-full overflow-auto p-4'>
                    <div className='flex flex-col h-full'>
                        {tab === "dashboard" && <Dashboard />}
                        {(tab === "reports" && player?.id) && <Reports mongo_player_id={player?.id}/>}
                    </div>
                </div>
            </div>  
        </main>
    </div>
    )
}

export default PlayerDetailPage